import {
    GlobalNotificationActionsNames,
    GlobalNotificationSecondaryComponentNames,
    GlobalNotificationStatus,
} from "@nova/component-global-notification"
import { AddOns, ID } from "@nova/constants"
import { getColorFromId } from "@nova/util-strings"
import { createAction, createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit"
import { clearExceedUsage } from "features/data-management/workspace/subscriptions/module"
import { pubsubTriggerNotification, setWatching, subscribeToMap } from "features/pubsub/slice"
import { get } from "nodash"
import { withActionPrefix } from "panels/state/constants"
import {
    AuthenticationThunkSuccessResponse,
    answerLoginChallenge,
    login,
    refreshCredentials,
    verifyAppleLogin,
    verifyMicrosoftLogin,
} from "user/authentication/thunks"
import { fetchMe } from "./thunks"
import { AppGlobalNotificationContext, ProfileSliceState } from "./types"
import { License, Viewer } from "@nova/schemas"

export const defaultNotificationContext = {
    message: "",
    status: GlobalNotificationStatus.default,
    timeoutInMs: 3000,
}
const initialState: ProfileSliceState = {
    isFetchingMe: false,
    hasInitiallyFetchedMe: false,
    id: undefined,
    email: undefined,
    groups: undefined,
    organizationIds: undefined,
    profilePhotoS3Key: undefined,
    defaultWorkspaceId: undefined,
    region: undefined,
    mapbox: {
        token: undefined,
    },
    license: undefined,
    liteLicense: undefined, //always keep the Lite on hand for a fallback
    isDomainWhiteListed: false,
    addOns: { colorfinder: false, hotspots: false, liveVideo: false },
    transitions: {
        pillPanelSliderTimeoutInMs: 1000,
        pillPanelTransitionDurationInMs: 300,
    },
    maximumPortfolioInvitationAmount: 30,
    globalNotificationContext: defaultNotificationContext,
}

const updateGlobalNotificationContext = (
    state: ProfileSliceState,
    action: PayloadAction<AppGlobalNotificationContext>
) => {
    const data = get(action, "payload")
    state.globalNotificationContext = data
}

const setLicense = (state: ProfileSliceState, action: PayloadAction<License | undefined>) => {
    //if your license gets changed, clear the local usage values
    clearExceedUsage()
    if (!action.payload) {
        state.license = state.liteLicense
        return
    }
    if (state.liteLicense?.licenseId === action.payload?.licenseId)
        state.liteLicense = action.payload
    state.license = action.payload
}

const setAddOn = (
    state: ProfileSliceState,
    action: PayloadAction<Partial<Record<AddOns, boolean>>>
) => {
    const [addOn, value] = Object.entries(action.payload)[0]
    state.addOns[addOn as AddOns] = value
}

const addUsage = (
    state: ProfileSliceState,
    action: PayloadAction<{ usageAmount: number; licenseId: ID }>
) => {
    const { usageAmount, licenseId } = action.payload

    //keep our record of the Lite license up to date if necessary
    if (licenseId === state.liteLicense?.licenseId) {
        state.liteLicense = {
            ...state.liteLicense,
            usageAmount: (state.liteLicense?.usageAmount || 0) + usageAmount,
        }
    }

    if (!state.license || licenseId !== state.license.licenseId) return

    state.license = {
        ...state.license,
        usageAmount: (state.license?.usageAmount || 0) + usageAmount,
    }
}

const getPreparingToFollowNotification = (viewer: Viewer) => ({
    message: `Preparing to Follow`,
    status: GlobalNotificationStatus.basic,
    color: getColorFromId(viewer.userId),
})

const preparingToFollowNotification = (state: ProfileSliceState, action: PayloadAction<Viewer>) => {
    state.globalNotificationContext = getPreparingToFollowNotification(action.payload)
}

export const setUserUnauthorized = createAction(withActionPrefix("revokeAuth"))

export const slice = createSlice({
    name: "profile",
    // need to spread it to insure the original object isn't captured so we can reuse it for the reset on logout!
    initialState: { ...initialState },
    reducers: {
        updateContext: updateGlobalNotificationContext,
        setLicense,
        setAddOn,
        addUsage,
        preparingToFollowNotification,
        setProfilePhotoS3Key: (state, action: PayloadAction<string>) => {
            state.profilePhotoS3Key = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(subscribeToMap, (state, action) => {
                state.globalNotificationContext = getPreparingToFollowNotification(action.payload)
            })
            .addCase(setWatching, (state, action) => {
                const viewer = action.payload
                if (viewer) return
                const context = state.globalNotificationContext
                if (context.kind !== "notification" && !context.timeoutInMs)
                    state.globalNotificationContext = defaultNotificationContext
            })
            .addCase(pubsubTriggerNotification, (state, action) => {
                const viewer = action.payload.viewer
                if (!viewer) {
                    state.globalNotificationContext =
                        action.payload.customContext || defaultNotificationContext
                    return
                }
                state.globalNotificationContext = {
                    message: `Following ${viewer.name}`,
                    status: GlobalNotificationStatus.basic,
                    timeoutAction: {
                        type: GlobalNotificationActionsNames.UNSUBSCRIBE_FROM_MAP,
                    },
                    color: getColorFromId(viewer.userId),
                    secondaryComponentName: GlobalNotificationSecondaryComponentNames.COPY_BUTTON,
                }
            })
            .addCase(fetchMe.pending, (state) => {
                state.isFetchingMe = true
                state.hasInitiallyFetchedMe = true
            })
            .addCase(fetchMe.fulfilled, (state, action) => {
                const payload = action.payload
                state.isFetchingMe = false
                state.id = payload.id
                state.email = payload.email
                state.firstName = payload.firstName
                state.lastName = payload.lastName
                state.groups = payload.groups
                state.profilePhotoS3Key = payload.profilePhotoS3Key
                state.defaultWorkspaceId = payload.defaultWorkspaceId
                state.license = payload.license
                state.liteLicense = payload.liteLicense
                state.isDomainWhiteListed = !!payload.isDomainWhiteListed
                state.addOns = {
                    hotspots: !!payload.addOns.find(
                        (addon) => addon.subscriptionType === AddOns.Hotspots
                    ),
                    colorfinder: !!payload.addOns.find(
                        (addon) => addon.subscriptionType === AddOns.Colorfinder
                    ),
                    liveVideo: !!payload.addOns.find(
                        (addon) => addon.subscriptionType === AddOns.LiveVideo
                    ),
                }
            })
            .addMatcher(
                isAnyOf(
                    login.fulfilled,
                    verifyAppleLogin.fulfilled,
                    verifyMicrosoftLogin.fulfilled,
                    refreshCredentials.fulfilled,
                    answerLoginChallenge.fulfilled
                ),
                (state, action) => {
                    const { payload } = action

                    // some small safeguards to ensure thunk-devs don't mess up by accident:
                    if (!payload.success) {
                        return
                    }

                    // type casting it to get auto completion, assuming the previous check is sufficient
                    const p = payload as AuthenticationThunkSuccessResponse
                    state.id = p.id
                    state.email = p.email
                    state.lastName = p.lastName
                    state.firstName = p.firstName
                }
            )
    },
})

const { actions, reducer } = slice

export const {
    updateContext: setGlobalNotificationContext,
    setLicense: setUserLicense,
    setAddOn: setUserAddOn,
    addUsage: addUsageToLicense,
    preparingToFollowNotification: openPreparingToFollowNotification,
    setProfilePhotoS3Key,
} = actions

export default reducer
